/**
 * Die Farben der Sprechblasen — eigene und fremde.
 *
 * **Leer heißt „wie das Erscheinungsbild".** Das ist kein Sonderfall aus
 * Bequemlichkeit, sondern der einzige Weg, die dunkle Darstellung heil zu
 * lassen: eine einmal gesetzte helle Farbe gälte sonst auch dort, und eine
 * weiße Blase auf dunklem Grund wäre eine Sackgasse. Deshalb wird eine
 * Variable nur gesetzt, wenn wirklich etwas gewählt wurde.
 */
export const BLASENFARBEN = [
  {
    key: 'chat_own_color',
    titel: 'Eigene Beiträge',
    hinweis: 'Rechts — deine, und beim Leser seine eigenen.',
    variable: '--bubble-out',
    schrift: '--bubble-text-own',
    gedaempft: '--bubble-muted-own',
    /**
     * Was das Farbfeld zeigt, solange nichts gewählt ist.
     *
     * **Hier und nicht in der Karte**, weil hier schon steht, welche Blase
     * gemeint ist. In `ChatfarbenCard` stand ein einziger Wert für beide
     * Felder -- solange beide Blasen hell waren, fiel das nicht auf; mit
     * einer blauen und einer grauen zeigte es an einem der beiden Felder
     * eine Farbe, die nirgends gilt.
     */
    vorgabe: '#007aff',
  },
  {
    key: 'chat_other_color',
    titel: 'Beiträge der anderen',
    hinweis: 'Links — die Antworten deiner Leser, und bei ihnen deine Beiträge.',
    variable: '--bubble-in',
    schrift: '--bubble-text-other',
    gedaempft: '--bubble-muted-other',
    vorgabe: '#e9e9eb',
  },
] as const

/** Der Name einer Blasenfarbe, wie ihn der Server kennt. */
export type Blasenfarbe = (typeof BLASENFARBEN)[number]['key']

/**
 * Schwarz oder Weiß — was auf dieser Farbe lesbar ist.
 *
 * **Sonst wäre die Farbwahl eine Falle.** Wer ein dunkles Blau wählt, bekäme
 * die dunkle Schrift des hellen Erscheinungsbilds darauf und läse nichts mehr.
 * Gerechnet wird die wahrgenommene Helligkeit (BT.601); der Schwellenwert ist
 * der übliche, und die Entscheidung ist grob genug, dass sie nie knapp
 * ausgeht.
 */
export function schriftAuf(hex: string): string {
  const zahl = Number.parseInt(hex.slice(1), 16)
  if (Number.isNaN(zahl)) return '#000000'
  const r = (zahl >> 16) & 255
  const g = (zahl >> 8) & 255
  const b = zahl & 255
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#000000' : '#ffffff'
}

/**
 * Dieselbe Farbe, nur zurückgenommen — für Uhrzeit, Haken und die Symbole.
 *
 * **Aus dem Betrieb:** „wenn die Farben der Chatblasen geändert werden,
 * berechne eine Farbe für die Symbole wie Löschen (sind aktuell grau), damit
 * sie sichtbar sind." Sie hingen an `--text-muted`, einem Grau der Seite — auf
 * einer dunkel gewählten Blase war davon nichts mehr zu sehen.
 *
 * **Kein zweites Helligkeitsurteil.** Was `schriftAuf()` sagt, gilt auch hier,
 * nur durchsichtig; zwei Schwellenwerte nebeneinander liefen irgendwann
 * auseinander. Als `rgba` und nicht als eigene Farbe — genau wie
 * `--accent-soft` in `knopffarbe.ts`.
 *
 * **Die zwei Werte sind verschieden, und das ist kein Versehen:** Weiß auf
 * dunklem Grund braucht mehr Deckung als Schwarz auf hellem, sonst verschwindet
 * es. Dieselbe Beobachtung steckt in den zwei Fassungen von `--text-muted`.
 */
export function gedaempftAuf(hex: string): string {
  return schriftAuf(hex) === '#ffffff'
    ? 'rgba(255, 255, 255, 0.72)'
    : 'rgba(17, 27, 33, 0.55)'
}

/**
 * **Die Kette: die eigene Ansicht schlägt die der Reise** (#52).
 *
 * Aus dem Betrieb: „Auch Mitlesende sollen die Farbe der Chatblasen einstellen
 * können … Voreinstellung ist die Einstellung des Reisenden." Die Reise trägt
 * seit #49 ihre eigenen Farben (beim Anlegen aus der Voreinstellung kopiert);
 * was ein Mitlesender für sich einstellt, legt sich darüber.
 *
 * `''` heißt jeweils „eine Stufe höher", ganz oben „wie das Erscheinungsbild".
 * Als reine Funktion, damit ein Lauf sie messen kann — wie `abstandAus()` in
 * `lib/tastatur.ts`.
 */
export function blasenKette(
  meine: { own: string | null, other: string | null } | null | undefined,
  reise: { own: string | null, other: string | null } | null | undefined,
): Record<Blasenfarbe, string> {
  return {
    chat_own_color: meine?.own || reise?.own || '',
    chat_other_color: meine?.other || reise?.other || '',
  }
}

/**
 * Die Variablen für einen gewählten Satz Farben.
 *
 * Was leer bleibt, kommt **nicht** vor: der Aufrufer nimmt eine fehlende
 * Variable wieder weg, statt sie auf einen Wert zu setzen. Nur so gilt wieder,
 * was das Erscheinungsbild vorgibt.
 */
export function blasenVariablen(
  werte: Partial<Record<Blasenfarbe, string>>,
): Record<string, string | null> {
  const raus: Record<string, string | null> = {}
  for (const eintrag of BLASENFARBEN) {
    const farbe = werte[eintrag.key] ?? ''
    raus[eintrag.variable] = farbe === '' ? null : farbe
    raus[eintrag.schrift] = farbe === '' ? null : schriftAuf(farbe)
    raus[eintrag.gedaempft] = farbe === '' ? null : gedaempftAuf(farbe)
  }
  return raus
}
